import type { ReactNode } from 'react';
import type { Locale } from '@/i18n';
import { useOptionalI18n } from '@/i18n';
import SearchableDropdown from './SearchableDropdown';

interface LanguageSwitcherProps {
  className?: string;
  dropdownClassName?: string;
  dropdownButtonClassName?: string;
  dropdownLabelClassName?: string;
  showChevron?: boolean;
  menuWidthMode?: 'reference' | 'content';
  menuMinWidth?: number;
  label?: string;
  onLocaleChange?: () => void;
  showLabel?: boolean;
  compact?: boolean;
  icon?: ReactNode;
  showMalay?: boolean;
}

function LanguageSwitcher({
  className = '',
  dropdownClassName = '',
  dropdownButtonClassName = '',
  dropdownLabelClassName = '',
  showChevron = true,
  menuWidthMode = 'reference',
  menuMinWidth,
  label,
  onLocaleChange,
  showLabel = true,
  compact = false,
  icon,
  showMalay = false,
}: LanguageSwitcherProps) {
  const { locale, setLocale, t } = useOptionalI18n();
  const resolvedLabel = label ?? t('i18n.languageSwitcher.label');
  const languageOptions: Array<{ value: Locale; label: string }> = [
    { value: 'en', label: compact ? 'EN' : t('i18n.languageSwitcher.english') },
    { value: 'zh-CN', label: compact ? '简' : t('i18n.languageSwitcher.simplifiedChinese') },
    ...(showMalay ? [{ value: 'ms' as Locale, label: compact ? 'BM' : t('i18n.languageSwitcher.malay') }] : []),
  ];

  const handleChange = (value: string | number) => {
    setLocale(value as Locale);
    onLocaleChange?.();
  };

  return (
    <div className={`relative ${className}`}>
      {showLabel && (
        <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
          {resolvedLabel}
        </span>
      )}
      {icon && (
        <span className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-gray-500">
          {icon}
        </span>
      )}
      <SearchableDropdown
        options={languageOptions}
        value={locale}
        onChange={handleChange}
        searchable={false}
        className={dropdownClassName}
        buttonClassName={dropdownButtonClassName}
        labelClassName={dropdownLabelClassName}
        showChevron={showChevron}
        menuWidthMode={menuWidthMode}
        menuMinWidth={menuMinWidth}
      />
    </div>
  );
}

export default LanguageSwitcher;
